import { cn } from '@mntn-dev/ui-utilities'
import { motion } from 'motion/react'
import type { KeyboardEvent, MouseEvent, PropsWithChildren } from 'react'
import { type TestIds, forwardRef, getTestProps } from '../../../utils/index.ts'
import type { MenuOptionIcon } from './types.ts'
import { getIconElement } from './utils.tsx'

export type MenuOptionProps = Readonly<
  PropsWithChildren<
    {
      className?: string
      disabled?: boolean
      label?: string
      leftIcon?: MenuOptionIcon
      rightIcon?: MenuOptionIcon
      selected?: boolean
      onClick?: () => void
    } & TestIds
  >
>

export const MenuOption = forwardRef<HTMLDivElement, MenuOptionProps>(
  (
    {
      children,
      className,
      dataTestId,
      dataTrackingId,
      disabled,
      label,
      leftIcon,
      rightIcon,
      selected,
      onClick,
    },
    ref
  ) => {
    const handleKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault()
        e.stopPropagation()
        onClick?.()
      }
    }

    const handleClick = (e: MouseEvent<HTMLDivElement>) => {
      e.stopPropagation()
      onClick?.()
    }

    const ariaLabel =
      (label ?? typeof children === 'string') ? children?.toString() : undefined

    const leftIconElement = getIconElement(leftIcon, disabled)
    const rightIconElement = getIconElement(rightIcon, disabled)

    return (
      <motion.div
        // biome-ignore lint/a11y/useSemanticElements: option is not correct html
        role="option"
        ref={ref}
        tabIndex={-1}
        aria-disabled={disabled}
        aria-label={ariaLabel}
        aria-selected={selected}
        onClick={handleClick}
        onKeyDown={handleKeyDown}
        initial={{
          backgroundColor: selected ? 'rgba(0, 0, 0, 0.9)' : 'rgba(0, 0, 0, 0)',
        }}
        whileHover={
          disabled
            ? undefined
            : {
                backgroundColor: 'rgba(0, 0, 0, 0.2)',
              }
        }
        whileTap={
          disabled
            ? undefined
            : {
                backgroundColor: 'rgba(0, 0, 0, 0.4)',
                scale: 0.98,
              }
        }
        transition={{ duration: 0.1 }}
        className={cn(
          'w-full flex items-center px-3 py-2 h-14 tracking-[0.03125em] active:bg-secondary-press',
          {
            'cursor-pointer': !!onClick && !disabled,
          },
          className
        )}
        {...getTestProps({ dataTestId, dataTrackingId })}
      >
        <div className="w-full flex items-center justify-between">
          <div className="flex items-center gap-3">
            {leftIconElement}
            <span
              className={cn(
                'text-primary font-base',
                disabled && 'text-disabled'
              )}
            >
              {children}
            </span>
          </div>
          {rightIconElement}
        </div>
      </motion.div>
    )
  }
)
